// components/Header/ClientSelector.tsx - Enhanced with service status
import React, { useState } from "react";
import { Client } from "../../types";
import { useAppStore } from "@/core/store/useAppStore";
import { CreateClientModal } from "@/domains/Client/components/CreateClientModal";
import { useClient } from "@/contexts/ClientContext";
import { ChevronDownIcon, PlusIcon } from "@/components/icons";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { BRAND_GRADIENT_TRACK } from "@/components/ui/brandGradient";

interface ClientSelectorProps {
  clients: Client[];
  selectedClient: Client | null;
  isLoading: boolean;
  onClientCreated?: (client: Client) => void;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({
  clients,
  selectedClient,
  isLoading,
  onClientCreated,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { setSelectedClient } = useAppStore();
  const { setClient } = useClient();

  const handleClientSelect = async (client: Client) => {
    setIsOpen(false);
    await setClient(client);
  };

  const handleCreateClient = () => {
    setIsOpen(false);
    setIsCreateModalOpen(true);
  };

  const handleClientCreated = (newClient: Client) => {
    console.log(`✅ New client created: ${newClient.clientName}`);
    onClientCreated?.(newClient);
    setSelectedClient(newClient);
  };

  if (isLoading) {
    return (
      <div className={`flex items-center space-x-2 px-3 py-2 ${BRAND_GRADIENT_TRACK} rounded-lg`}>
        <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-gray-600">Loading clients...</span>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className={`px-3 py-2 ${BRAND_GRADIENT_TRACK} rounded-lg`}>
        <span className="text-sm text-gray-500">No clients found</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 text-sm ${BRAND_GRADIENT_TRACK} hover:bg-white/40 rounded-lg transition-colors max-w-[40vw] md:max-w-none`}
      >
        <span className="font-medium truncate">
          {selectedClient?.clientName || "Select Client"}
        </span>

        <ChevronDownIcon className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <>
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-30 max-h-96 overflow-y-auto">
            <div className="py-1">
              {clients.filter((c) => c.status !== "ARCHIVED").map((client) => (
                <div
                  key={client.id}
                  className={`flex items-center justify-between px-4 py-3 text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    selectedClient?.id === client.id
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700"
                  }`}
                >
                  {/* ✅ Main client button */}
                  <button
                    onClick={() => handleClientSelect(client)}
                    className="flex-1 text-left"
                  >
                    <div className="font-medium">{client.clientName}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {client?.stats?.totalImages} images
                    </div>

                  </button>
                </div>
              ))}

              {/* Create New Client Option */}
              <div className="border-t border-gray-200 pt-1">
                <button
                  onClick={handleCreateClient}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <PlusIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-blue-700">
                      {ENTITY_CONFIG.client.createLabel}
                    </div>
                    <div className="text-xs text-gray-500">
                      Set up a new client workspace
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <CreateClientModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onClientCreated={handleClientCreated}
      />
    </div>
  );
};
